import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  alpha,
  Divider
} from '@mui/material'
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { motion } from 'framer-motion'
import NotificationPanel from '../NotificationPanel'
import { crmColors } from '../../theme/colors'

const Header = ({ onMenuClick }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, logout } = useAuth()
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [logoError, setLogoError] = useState(false)

  // Listen for custom event to open notifications
  React.useEffect(() => {
    const handleOpenNotifications = () => {
      setNotificationOpen(true)
    }
    window.addEventListener('openNotifications', handleOpenNotifications)
    return () => {
      window.removeEventListener('openNotifications', handleOpenNotifications)
    }
  }, [])

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    handleProfileMenuClose()
  }

  const handleSearch = (event) => {
    event.preventDefault()
    // Implement search functionality
    console.log('Searching for:', searchQuery)
  }

  return (
    <AppBar
      className="app-header"
      position="static"
      elevation={0}
      sx={{
        backgroundColor: crmColors.primary.darker,
        borderBottom: `1px solid ${crmColors.primary.darker}`,
        color: '#ffffff',
        background: `linear-gradient(135deg, ${crmColors.primary.dark} 0%, ${crmColors.primary.darker} 100%)`,
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.25)',
      }}
    >
      <Toolbar sx={{ 
        minHeight: '88px !important', 
        py: 2.5, 
        height: '88px', 
        maxHeight: '88px',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'nowrap'
      }}>
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 2 }}>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={onMenuClick}
              sx={{ color: '#ffffff' }}
              >
                <MenuIcon />
              </IconButton>
            </motion.div>
          
          {!logoError ? (
            <Box
              component="img"
              src="/logo.png"
              alt="EduOK CRM Logo"
              onError={() => setLogoError(true)}
              sx={{
                height: { xs: 32, md: 40 },
                width: 'auto',
                objectFit: 'contain',
                cursor: 'pointer'
              }}
            />
          ) : (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#ffffff',
                whiteSpace: 'nowrap'
            }}
          >
            EduOK CRM
          </Typography>
          )}
        </Box>

        {/* Center Section - Search */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flex: 1,
            maxWidth: 600,
            minWidth: 0
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              position: 'relative',
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              <SearchIcon />
            </Box>
            <InputBase
              placeholder="Search schools, clients, training..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: '100%',
                pl: 5,
                pr: 2,
                py: 1.5,
                fontSize: '0.875rem',
                color: '#ffffff',
                '& input': {
                  color: '#ffffff',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Notifications */}
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <IconButton
              color="inherit"
              onClick={() => setNotificationOpen(true)}
              sx={{ position: 'relative', color: '#ffffff' }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </motion.div>

          {/* User Menu */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{ ml: 1 }}
            >
              <Avatar
                src={user?.avatar}
                sx={{ width: 32, height: 32 }}
              >
                {user?.name?.charAt(0)}
              </Avatar>
            </IconButton>
          </motion.div>

          {/* User Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: theme.shadows[8],
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user?.email}
              </Typography>
            </Box>
            
            <MenuItem onClick={handleProfileMenuClose}>
              <PersonIcon sx={{ mr: 2 }} />
              Profile
            </MenuItem>
            
            <MenuItem onClick={handleProfileMenuClose}>
              <SettingsIcon sx={{ mr: 2 }} />
              Settings
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <NotificationPanel open={notificationOpen} onClose={() => setNotificationOpen(false)} />
    </AppBar>
  )
}

export default Header
